// axios.js
import axios from 'axios'
import { getActivePinia } from 'pinia'
import { authContextEpoch, onAuthContextChanged } from '@/@vxapp/Auth/authContext'
import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { Endpoint } from '@/@vxapp/@Enums/Endpoint'
import { Header } from '@/@vxapp/@Enums/Header'
import { isClientError, isServerError, toAppHttpError } from '@/@vxapp/@Utils/http'
import { useApiCacheStore } from '@/@vxapp/@Utils/useApiCacheStore'
import { useRouterStore } from '@/@vxapp/@Utils/useRouterStore'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { isAuthLogoutPending } from '@/@vxapp/Auth/authRefreshCoordinator'
import { classifySessionEnd } from '@/@vxapp/Auth/sessionEnded'
import { LogConfig } from '@/@vxapp/@core/Config/LogConfig'
import { router as appRouter } from '@/plugins/2.router'

/*
const logConfig = {
  name: 'axios',
  debugLogging: false,
  warnLogging: true,
  errorLogging: true,
}*/

// Track in-flight requests to deduplicate simultaneous duplicate requests
const inFlightRequests = new Map()

onAuthContextChanged(() => {
  inFlightRequests.clear()
  if (getActivePinia()) useApiCacheStore().clear()
})

function isConditionalBusinessRequest(config) {
  return (
    /^\/(?:v2\/)?(?:Quotes|Invoices|Orders|Messages)(?:\/|$)/.test(normalizePath(config)) ||
    /^\/(?:v2\/)?Emails\/Messages(?:\/|$)/.test(normalizePath(config))
  )
}

function rejectReplacedContext(config) {
  if (config?._authContextEpoch !== undefined && config._authContextEpoch !== authContextEpoch()) {
    const error = new axios.CanceledError('Permission context changed.')

    error.config = config
    config._inFlightReject?.(error)
    throw error
  }
}

function isGuestServiceRead(config) {
  return (
    config.method === 'get' &&
    /^\/(?:v2\/)?(?:Quotes|Invoices|Orders)\/[^/]+$/.test(normalizePath(config)) &&
    !normalizePath(config).endsWith('/load_new')
  )
}

function hasRecoveredGuestRead(config, authStore) {
  const context = config?._guestReadContext

  return Boolean(
    context &&
    authStore.isGuest &&
    !authStore.userData &&
    authStore.api === context.api &&
    authStore.token &&
    authStore.token !== context.token &&
    authStore._authLifecycleVersion === context.version + 1 &&
    authStore.isAuthLifecycleCurrent(context.version + 1)
  )
}

async function recoverGuestRead(config, authStore, reason) {
  const context = config?._guestReadContext
  if (
    !reason ||
    !context ||
    config._guestRecoveryAttempted ||
    !authStore.isGuest ||
    authStore.api !== context.api ||
    authStore._authLifecycleVersion !== context.version
  )
    return false

  config._guestRecoveryAttempted = true

  const recovered = await authStore.endSession(reason)
  if (!recovered || !hasRecoveredGuestRead(config, authStore)) return false

  config._authContextEpoch = authContextEpoch()

  return true
}

// Lazy initialization of router navigation tracking using the already-created router instance.
function initializeNavigationTracking() {
  const routerStore = useRouterStore()

  routerStore.initializeNavigationTracking(appRouter)
}

const axiosIns = axios.create({
  baseURL: Endpoint.Vxapp.Api.url,

  //timeout: Endpoint.Vxapp.Api.timeout,
  timeout: 100000,
  withCredentials: true, // Required for CORS with custom headers
})

function log() {
  /*
  if (level === 'd' && logConfig.debugLogging) {
    console.debug(`[axios] ${level} ${message}`, context)
  }

  if (level === 'w' && logConfig.warnLogging) {
    console.warn(`[axios] ${level} ${message}`, context)
  }

  if (level === 'e' && logConfig.errorLogging) {
    console.error(`[axios] ${level} ${message}`, context)
  }*/
}

function safeDeepCopy(obj) {
  try {
    if (typeof structuredClone === 'function') return structuredClone(obj)
  } catch {
    // Fall back to JSON for values structuredClone cannot copy.
  }
  try {
    return JSON.parse(JSON.stringify(obj))
  } catch {
    return obj
  }
}

function normalizePath(config) {
  const rawUrl = config?.url ? String(config.url) : ''

  return rawUrl.replace(/^https?:\/\/[^/]+/, '')
}

/**
 * Validates if a string is a valid GUID/UUID format
 * @param {string} guid - The GUID string to validate
 * @returns {boolean} - True if valid GUID format, false otherwise
 */
function isValidGuid(guid) {
  if (!guid || typeof guid !== 'string') {
    return false
  }

  // Standard GUID/UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  // 8-4-4-4-12 hexadecimal digits
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  return guidRegex.test(guid)
}

/**
 * Validates if an EndpointName exists in the Endpoint enum
 * @param {string} endpointName - The endpoint name to validate
 * @returns {boolean} - True if endpoint exists, false otherwise
 */
function isValidEndpointName(endpointName) {
  if (!endpointName || typeof endpointName !== 'string') {
    return false
  }

  // Check if endpointName exists as a key in the Endpoint object
  try {
    return (
      endpointName in Endpoint &&
      Endpoint[endpointName] !== null &&
      Endpoint[endpointName] !== undefined
    )
  } catch (err) {
    log('w', '[axios][cache] Error validating endpoint name', { endpointName, error: err })

    return false
  }
}

/**
 * Validates if an endpoint segment is in a valid format for cache invalidation purposes.
 * This is used instead of isValidEndpointName for cache invalidation because many valid
 * endpoints are not defined in the Endpoint enum (e.g., RevenueStreams, Products, Assets).
 *
 * Uses format-based validation rather than enum membership to ensure cache invalidation
 * works for all dynamically-used endpoints.
 *
 * @param {string} segment - The endpoint segment to validate
 * @returns {boolean} - True if segment is a valid endpoint format, false otherwise
 */
function isValidEndpointSegment(segment) {
  if (!segment || typeof segment !== 'string') {
    return false
  }

  // Valid endpoint segments:
  // - Must start with an uppercase or lowercase letter
  // - Can contain letters, numbers, underscores, and hyphens
  // - Examples: "RevenueStreams", "Products", "JobTypes", "Messages", "Assets"
  // - Excludes: GUIDs (already filtered by isValidGuid), numeric strings, special chars
  return /^[A-Z][\w-]*$/i.test(segment)
}

/**
 * Extracts EndpointName and GUID from a path matching the MESSAGES_VARIABLES_ENDPOINT_PATTERN
 * Pattern: /{EndpointName}/Messages/Variables/{guid}
 * @param {string} path - The normalized path to check
 * @returns {{endpointName: string|null, guid: string|null}|null} - Extracted values or null if no match
 */
function extractMessagesVariablesPattern(path) {
  if (!path || typeof path !== 'string') {
    return null
  }

  // Remove query string and fragment from path before matching for exact pattern check
  const pathWithoutQuery = path.split('?')[0].split('#')[0]

  // Match pattern: /{EndpointName}/Messages/Variables/{guid}
  // Example: /Products/Messages/Variables/123e4567-e89b-12d3-a456-426614174000
  // Use regex with anchors (^ and $) to ensure exact match with no extra segments
  const match = pathWithoutQuery.match(/^\/([^/]+)\/Messages\/Variables\/([^/]+)$/)
  if (!match) {
    return null
  }

  const endpointName = match[1] || null
  const guid = match[2] || null

  // Validate both EndpointName and GUID
  if (!endpointName || !guid) {
    return null
  }

  if (!isValidGuid(guid)) {
    log('d', '[axios][cache] Invalid GUID in Messages/Variables pattern', { path, guid })

    return null
  }

  if (!isValidEndpointName(endpointName)) {
    log('d', '[axios][cache] Invalid EndpointName in Messages/Variables pattern', {
      path,
      endpointName,
    })

    return null
  }

  return { endpointName, guid }
}

/**
 * Extracts EndpointName and GUID from a path matching the FETCH_SERVICE_ENDPOINT_PATTERN
 * Pattern: /{EndpointName}/{guid}
 * @param {string} path - The normalized path to check
 * @returns {{endpointName: string|null, guid: string|null}|null} - Extracted values or null if no match
 */
function extractFetchServicePattern(path) {
  if (!path || typeof path !== 'string') {
    return null
  }

  // Remove query string and fragment from path before matching for exact pattern check
  const pathWithoutQuery = path.split('?')[0].split('#')[0]

  // Match pattern: /{EndpointName}/{guid}
  // Example: /Products/123e4567-e89b-12d3-a456-426614174000
  // Must have exactly 2 segments (EndpointName and GUID)
  // Use regex for exact match to ensure no extra path segments
  const match = pathWithoutQuery.match(/^\/([^/]+)\/([^/]+)$/)
  if (!match) {
    return null
  }

  const endpointName = match[1] || null
  const guid = match[2] || null

  if (!endpointName || !guid) {
    return null
  }

  // Validate both EndpointName and GUID
  if (!isValidGuid(guid)) {
    log('d', '[axios][cache] Invalid GUID in FetchService pattern', { path, guid })

    return null
  }

  if (!isValidEndpointName(endpointName)) {
    log('d', '[axios][cache] Invalid EndpointName in FetchService pattern', { path, endpointName })

    return null
  }

  return { endpointName, guid }
}

/**
 * Validates if a metricType is in a valid format
 * @param {string} metricType - The metric type string to validate
 * @returns {boolean} - True if valid metric type format, false otherwise
 */
function isValidMetricType(metricType) {
  if (!metricType || typeof metricType !== 'string') {
    return false
  }

  // Metric type should be a non-empty string
  // Allow alphanumeric characters, underscores, and hyphens
  // Examples: "Weekly", "Monthly", "StatusMetrics", "Custom_Metric", "metric-name"
  const metricTypeRegex = /^[\w-]+$/

  return metricTypeRegex.test(metricType) && metricType.length > 0 && metricType.length <= 100
}

/**
 * Extracts EndpointName and metricType from a path matching the METRICS_ENDPOINT_PATTERN
 * Pattern: /{EndpointName}/Metrics/{metricType}
 * @param {string} path - The normalized path to check
 * @returns {{endpointName: string|null, metricType: string|null}|null} - Extracted values or null if no match
 */
function extractMetricsPattern(path) {
  if (!path || typeof path !== 'string') {
    return null
  }

  // Remove query string and fragment from path before matching for exact pattern check
  const pathWithoutQuery = path.split('?')[0].split('#')[0]

  // Match pattern: /{EndpointName}/Metrics/{metricType}
  // Examples:
  //   /Messages/Metrics/Weekly
  //   /Messages/Metrics/Monthly
  //   /Invoices/Metrics/StatusMetrics
  //   /Quotes/Metrics/StatusMetrics
  // Use regex with anchors (^ and $) to ensure exact match with no extra segments
  const match = pathWithoutQuery.match(/^\/([^/]+)\/Metrics\/([^/]+)$/)
  if (!match) {
    return null
  }

  const endpointName = match[1] || null
  const metricType = match[2] || null

  // Validate both EndpointName and metricType
  if (!endpointName || !metricType) {
    return null
  }

  if (!isValidMetricType(metricType)) {
    log('d', '[axios][cache] Invalid metricType in Metrics pattern', { path, metricType })

    return null
  }

  if (!isValidEndpointName(endpointName)) {
    log('d', '[axios][cache] Invalid EndpointName in Metrics pattern', { path, endpointName })

    return null
  }

  return { endpointName, metricType }
}

/**
 * Extracts whether a path is an exact load_new template endpoint.
 * Only terminal /load_new routes are cacheable; derived routes such as
 * /Invoices/load_new/From/Quotes/{guid} must bypass the load_new cache.
 *
 * Examples:
 * - /Invoices/load_new -> true
 * - /Messages/Templates/load_new -> true
 * - /Invoices/load_new/From/Quotes/123 -> false
 *
 * @param {string} path - The normalized path to check
 * @returns {boolean} - True if path is an exact load_new template route
 */
function extractLoadNewPattern(path) {
  if (!path || typeof path !== 'string') {
    return false
  }

  // Remove query string and fragment, then normalize trailing slashes.
  const pathWithoutQuery = path.split('?')[0].split('#')[0].replace(/\/+$/g, '')
  const segments = pathWithoutQuery.split('/').filter(Boolean)

  if (segments.length < 2) {
    return false
  }

  // Only cache terminal /load_new endpoints. Any extra segment after load_new
  // indicates a derived template route that should bypass the generic template cache.
  return segments[segments.length - 1] === 'load_new'
}

/**
 * Extracts the base endpoint name from a path for cache invalidation
 * Used to find corresponding load_new cache entries when POST requests are made
 * Handles both simple and nested endpoint patterns:
 * - Simple: /{EndpointName} or /{EndpointName}/{guid} -> EndpointName
 * - Nested: /{Parent}/{Child} or /{Parent}/{Child}/{guid} -> Parent/Child
 *
 * Examples:
 * - /Messages -> Messages
 * - /Messages/Templates -> Messages/Templates
 * - /Assets/Services -> Assets/Services
 * - /Products/123-456-789 -> Products (GUID excluded)
 * - /Assets/Services/123-456-789 -> Assets/Services (GUID excluded)
 *
 * @param {string} path - The normalized path to extract from
 * @returns {string|null} - The endpoint name (including nested path) or null if not extractable
 */
function extractBaseEndpointName(path) {
  if (!path || typeof path !== 'string') {
    return null
  }

  // Remove query string and fragment from path
  const pathWithoutQuery = path.split('?')[0].split('#')[0]

  // Get segments from path (e.g., /Assets/Services/123 -> ['Assets', 'Services', '123'])
  const segments = pathWithoutQuery.split('/').filter(Boolean)

  if (segments.length === 0) {
    return null
  }

  // Check if the last segment is a GUID - if so, exclude it
  // This handles paths like /Products/123-456-789 where we want 'Products'
  // and paths like /Assets/Services/123-456-789 where we want 'Assets/Services'
  const lastSegment = segments[segments.length - 1]
  if (isValidGuid(lastSegment)) {
    segments.pop()
  }

  if (segments.length === 0) {
    return null
  }

  // Validate the first segment is a valid endpoint format
  // Uses format-based validation instead of Endpoint enum membership to ensure
  // cache invalidation works for all endpoints (e.g., RevenueStreams, Products)
  // that may not be defined in the Endpoint enum
  if (!isValidEndpointSegment(segments[0])) {
    return null
  }

  // Return the full path as the endpoint name (e.g., 'Assets/Services', 'Messages/Templates')
  // This allows proper cache invalidation for nested endpoints
  return segments.join('/')
}

/**
 * Invalidates the load_new cache entry for a given endpoint
 * Called when a POST request is made to an endpoint to ensure fresh templates
 * Uses prefix-based invalidation to match ALL cache entries for this endpoint's load_new
 * regardless of query parameters (e.g., populateDefaults, withDefaults)
 * @param {string} endpointName - The endpoint name (e.g., 'Products', 'Messages', 'Messages/Templates')
 * @returns {boolean} - True if a cache entry was invalidated, false otherwise
 */
function invalidateLoadNewCache(endpointName) {
  if (!endpointName || typeof endpointName !== 'string') {
    return false
  }

  try {
    const cacheStore = useApiCacheStore()

    // Use prefix-based invalidation to match ALL cache entries for this endpoint's load_new
    // This handles entries cached with any query params (populateDefaults, withDefaults, etc.)
    // Cache key format: "{method}::{path}::{params}"
    // Prefix to match: "get::/{endpointName}/load_new::"
    const cacheKeyPrefix = `get::/${endpointName}/load_new::`

    // clearNamespace removes ALL entries starting with the prefix
    const removedCount = cacheStore.clearNamespace(cacheKeyPrefix)

    if (removedCount > 0) {
      cacheLog(
        `Invalidated load_new cache on POST - Endpoint: ${endpointName} | Removed: ${removedCount} entries | Prefix: ${cacheKeyPrefix}`,
        'delete'
      )

      return true
    } else {
      // No entries found is OK - the cache may not have been populated yet
      cacheLog(
        `No load_new cache to invalidate - Endpoint: ${endpointName} | Prefix: ${cacheKeyPrefix}`,
        'check'
      )

      return false
    }
  } catch (err) {
    cacheLog(
      `Error invalidating load_new cache - Endpoint: ${endpointName} | Error: ${err?.message || String(err)}`,
      'error'
    )

    return false
  }
}

/**
 * Checks if a path matches one of the caching patterns and returns cache info
 * @param {string} path - The normalized path to check
 * @returns {{shouldCache: boolean, ttl: number|null}} - Cache eligibility and TTL in milliseconds
 */

function getCacheInfo(path) {
  if (
    /^\/(?:v2\/)?(?:TenantUsers|TenantRoles|TenantRolePermissions|TenantPermissionDefinitions)(?:\/|$)/.test(
      path
    )
  ) {
    return { shouldCache: false }
  }
  if (!path || typeof path !== 'string') {
    return { shouldCache: false, ttl: null }
  }

  if (/(?:^|\/)Calendar(?:\/|$)/.test(path.split(/[?#]/)[0])) {
    return { shouldCache: false, ttl: null }
  }

  // Check for exact load_new template routes (existing caching - 24 hours).
  // Derived routes like /Invoices/load_new/From/Quotes/{guid} must bypass cache.
  if (extractLoadNewPattern(path)) {
    return { shouldCache: true, ttl: 86400000 } // 24 hours = 24 * 60 * 60 * 1000 ms
  }

  // Check for MESSAGES_VARIABLES_ENDPOINT_PATTERN: /{EndpointName}/Messages/Variables/{guid}
  const messagesVarsMatch = extractMessagesVariablesPattern(path)

  if (messagesVarsMatch) {
    return { shouldCache: true, ttl: 5000 }
  }

  // Check for FETCH_SERVICE_ENDPOINT_PATTERN: /{EndpointName}/{guid}
  const fetchServiceMatch = extractFetchServicePattern(path)
  if (fetchServiceMatch) {
    return { shouldCache: true, ttl: 5000 }
  }

  // Check for METRICS_ENDPOINT_PATTERN: /{EndpointName}/Metrics/{metricType}
  // Examples: /Invoices/Metrics/StatusMetrics, /Messages/Metrics/Monthly, /Calendar/Metrics/UpcomingEntries
  const metricsMatch = extractMetricsPattern(path)
  if (metricsMatch) {
    return { shouldCache: true, ttl: 86400000 } // 24 hours = 24 * 60 * 60 * 1000 ms
  }

  return { shouldCache: false, ttl: null }
}

function extractGuidFromMessagesPath(path) {
  // Legacy function - kept for backward compatibility
  // Also check new pattern
  const messagesVarsMatch = extractMessagesVariablesPattern(path)
  if (messagesVarsMatch) {
    return messagesVarsMatch.guid
  }

  // Original pattern check
  const m = path.match(/^\/Products\/Messages\/Variables\/(.+)$/)

  return m ? m[1] : null
}

function cacheLog(message, color = 'default') {
  if (!LogConfig().CacheLogging()) return

  const colorMap = {
    default: 'color: #888;',
    get: 'color: #3784ff;', // Blue
    post: 'color: #36ad4b;', // Green
    put: 'color: #e6ca32;', // Yellow
    delete: 'color: #e14646;', // Red
    patch: 'color: #b66aec;', // Purple
    cached: 'color: #18bbb0;', // Cyan - Cache hit/retrieved
    save: 'color: #36ad4b;', // Green - Saved to cache
    miss: 'color: #f59e0b;', // Orange - Cache miss
    check: 'color: #6b7280;', // Gray - Checking cache
    error: 'color: #ef4444;', // Red - Cache error
  }

  // Fallback to gray if method not in map
  const style = colorMap[color] || colorMap.default

  console.debug(`%c[CACHE] ${message}`, style)
}

// Early request interceptor to short-circuit cached GETs
axiosIns.interceptors.request.use(
  config => {
    try {
      const method = config?.method ? String(config.method).toLowerCase() : 'get'
      if (method !== 'get') return config

      const path = normalizePath(config)
      const cacheInfo = getCacheInfo(path)

      if (!cacheInfo.shouldCache) {
        return config
      }

      const cacheStore = useApiCacheStore()
      const cacheKey = cacheStore.generateKey('get', path, config?.params)
      const hasValid = cacheStore.hasValid(cacheKey)

      cacheLog(`Checking cache - Path: ${path} | Key: ${cacheKey} | Valid: ${hasValid}`, 'check')

      // Check for in-flight duplicate request (deduplication)
      if (inFlightRequests.has(cacheKey)) {
        const inFlightPromise = inFlightRequests.get(cacheKey)

        cacheLog(
          `Deduplicating request - Path: ${path} | Key: ${cacheKey} | Waiting for in-flight request`,
          'check'
        )

        // Mark as from cache to prevent re-caching in response interceptor
        config._fromCache = true

        // Return a promise that resolves when the in-flight request completes
        config.adapter = () =>
          inFlightPromise
            .then(response => {
              cacheLog(
                `Served from in-flight request - Path: ${path} | Key: ${cacheKey} | Status: ${response.status}`,
                'cached'
              )

              // Deep copy the response data to prevent mutations
              return {
                ...response,
                data: safeDeepCopy(response.data),
              }
            })
            .catch(err => {
              cacheLog(
                `In-flight request failed - Path: ${path} | Key: ${cacheKey} | Error: ${err?.message || String(err)}`,
                'error'
              )
              throw err
            })

        return config
      }

      if (!hasValid) {
        cacheLog(`Cache miss or expired - Path: ${path} | Key: ${cacheKey}`, 'miss')

        // Create a promise that will be resolved when the response arrives
        // This allows duplicate requests to wait for the same network call
        let resolvePromise = null
        let rejectPromise = null

        const inFlightPromise = new Promise((resolve, reject) => {
          resolvePromise = resolve
          rejectPromise = reject
        })

        // Cancellation may have no duplicate consumer waiting for this promise.
        inFlightPromise.catch(() => {})

        // Store the promise for deduplication
        inFlightRequests.set(cacheKey, inFlightPromise)

        // Store resolve/reject on the config so response interceptor can use them
        config._inFlightResolve = resolvePromise
        config._inFlightReject = rejectPromise
        config._inFlightCacheKey = cacheKey

        // Clean up in-flight tracking after a timeout to prevent memory leaks
        // (response interceptor should clean it up, but this is a safety net)
        setTimeout(() => {
          if (inFlightRequests.get(cacheKey) === inFlightPromise) {
            inFlightRequests.delete(cacheKey)
          }
        }, 30000) // 30 second timeout

        return config
      }

      const cached = cacheStore.get(cacheKey)
      if (!cached) {
        cacheLog(
          `Cache entry not found after hasValid check - Path: ${path} | Key: ${cacheKey}`,
          'miss'
        )

        return config
      }

      // GUID safety: if request has GUID, ensure cached response matches
      // Check both old pattern and new pattern
      const requestGuid = extractGuidFromMessagesPath(path)

      // Also check for FetchService pattern GUID
      const fetchServiceMatch = extractFetchServicePattern(path)
      const fetchServiceGuid = fetchServiceMatch ? fetchServiceMatch.guid : null
      const actualRequestGuid = requestGuid || fetchServiceGuid

      if (actualRequestGuid) {
        const responseGuid =
          cached?.data?.serviceData?.PRODUCT_GUID ??
          cached?.data?.serviceData?.GUID ??
          cached?.data?.guid ??
          null

        if (responseGuid && actualRequestGuid !== responseGuid) {
          cacheLog(
            `GUID mismatch, bypassing cache - Path: ${path} | Request GUID: ${actualRequestGuid} | Response GUID: ${responseGuid}`,
            'error'
          )

          return config
        }
      }

      cacheLog(`Retrieved from cache - Path: ${path} | Key: ${cacheKey}`, 'cached')

      // Mark this config as coming from cache
      config._fromCache = true
      config.adapter = () => {
        cacheLog(
          `Served from cache - Path: ${path} | Key: ${cacheKey} | Status: ${cached.status || 200}`,
          'cached'
        )

        return Promise.resolve({
          data: safeDeepCopy(cached.data),
          status: cached.status || 200,
          statusText: cached.statusText || 'OK',
          headers: cached.headers || {},
          config,
          request: {},
        })
      }
    } catch (err) {
      cacheLog(
        `Request interceptor cache error, proceeding without cache - Error: ${err?.message || String(err)}`,
        'error'
      )

      return config
    }

    return config
  },
  error => {
    log('e', '[axios] Request interceptor error', error)

    return Promise.reject(error)
  }
)

axiosIns.interceptors.request.use(
  async config => {
    if (
      config.currentSessionLogout === true &&
      config.method === 'post' &&
      config.url === '/Kernal/Auth/Logout'
    )
      return config
    if (isConditionalBusinessRequest(config)) config._authContextEpoch = authContextEpoch()
    initializeNavigationTracking()

    const authStore = useAuthStore()
    if (isGuestServiceRead(config) && authStore.isGuest && !config._guestReadContext) {
      config._guestReadContext = {
        api: authStore.api,
        token: authStore.token,
        version: authStore._authLifecycleVersion,
      }
    }

    const isRenewal =
      config.method === Endpoint.Auth.refresh.method && config.url === Endpoint.Auth.refresh.url

    const isLogin =
      config.method === Endpoint.Auth.login.method && config.url === Endpoint.Auth.login.url

    if (isLogin) {
      config.headers.delete([Header.Auth, Header.Api])

      return config
    }

    // Public password-reset calls carry no session. Request is tenant-less; Validate and
    // Confirm keep only the tenant header the caller sets explicitly from the emailed link.
    const passwordReset = Endpoint.Auth.PasswordReset

    const isPublicPasswordReset =
      config.method === 'post' &&
      [passwordReset.Request.url, passwordReset.Validate.url, passwordReset.Confirm.url].includes(
        config.url
      )

    if (isPublicPasswordReset) {
      config.headers.delete(Header.Auth)
      if (config.url === passwordReset.Request.url) config.headers.delete(Header.Api)

      return config
    }

    const isGuest =
      config.method === Endpoint.Auth.guests.method && config.url === Endpoint.Auth.guests.url

    const failure = () => ({
      responseCode: ResponseCode.UNAUTHORIZED,
      message: 'Your session could not be renewed. Sign in again.',
      data: { error: 'authentication_required' },
    })

    if (!isGuest) {
      if (authStore.isLoggingOut || isAuthLogoutPending()) throw failure()
      if (!isRenewal) {
        const version = authStore._authLifecycleVersion
        const result = await authStore.ensureSession()
        if (result.guestRecovered) {
          if (config._guestRecoveryAttempted || !hasRecoveredGuestRead(config, authStore))
            throw failure()
          config._guestRecoveryAttempted = true
          config._authContextEpoch = authContextEpoch()
        } else if (!result.success || !authStore.isAuthLifecycleCurrent(version)) throw failure()
      }
    }

    rejectReplacedContext(config)

    const headers = authStore.getHeaders()
    if (headers && typeof headers === 'object' && !Array.isArray(headers)) {
      config.headers = config.headers || {}
      Object.entries(headers).forEach(([key, value]) => {
        if (value != null) config.headers[key] = value
      })
    }

    return config
  },
  error => {
    log('e', '[axios] Request interceptor error', error)

    return Promise.reject(error)
  }
)

axiosIns.interceptors.response.use(
  async response => {
    rejectReplacedContext(response.config)
    if (
      response.config._guestRecoveryAttempted &&
      hasRecoveredGuestRead(response.config, useAuthStore())
    ) {
      response.guestRecoveryEpoch = authContextEpoch()
    }

    const fromCache = response.config._fromCache === true

    // Invalidate load_new cache on successful POST requests
    // This ensures that after creating a new entity, the next load_new request
    // will fetch a fresh template with a new GUID instead of using a stale cached template
    const method = response.config.method ? String(response.config.method).toLowerCase() : 'get'
    if (method === 'post' && response.status >= 200 && response.status < 300) {
      const path = normalizePath(response.config)
      const endpointName = extractBaseEndpointName(path)
      if (endpointName) {
        invalidateLoadNewCache(endpointName)
      } else {
        // Defensive logging: warn when endpoint name cannot be extracted
        // This helps identify any edge cases where cache invalidation may be skipped
        cacheLog(
          `Warning: Could not extract endpoint name from path: ${path} - cache invalidation skipped`,
          'error'
        )
      }
    }

    // Invalidate fetch and messages-variables caches on successful PATCH requests
    // This ensures that after updating an entity, the next GET for that entity
    // and its message variables will return fresh data instead of stale cached responses
    if (method === 'patch' && response.status >= 200 && response.status < 300) {
      const path = normalizePath(response.config)
      const fetchServiceMatch = extractFetchServicePattern(path)
      if (fetchServiceMatch) {
        const { endpointName, guid } = fetchServiceMatch
        const cacheStore = useApiCacheStore()

        const fetchCacheKeyPrefix = `get::/${endpointName}/${guid}::`

        cacheStore.clearNamespace(fetchCacheKeyPrefix)

        const msgVarsCacheKeyPrefix = `get::/${endpointName}/Messages/Variables/${guid}::`

        cacheStore.clearNamespace(msgVarsCacheKeyPrefix)

        cacheLog(`Invalidated caches on PATCH - Endpoint: ${endpointName} | GUID: ${guid}`, 'patch')
      }
    }

    // Handle in-flight request deduplication
    const inFlightCacheKey = response.config._inFlightCacheKey
    if (inFlightCacheKey && inFlightRequests.has(inFlightCacheKey)) {
      const resolveInFlight = response.config._inFlightResolve
      const rejectInFlight = response.config._inFlightReject

      if (response.status >= 200 && response.status < 300) {
        // Save to cache FIRST before resolving in-flight requests
        // This ensures any new requests that come in can use the cache immediately
        try {
          const method = response.config.method
            ? String(response.config.method).toLowerCase()
            : 'get'

          if (method === 'get') {
            const path = normalizePath(response.config)
            const cacheInfo = getCacheInfo(path)

            if (cacheInfo.shouldCache) {
              const cacheStore = useApiCacheStore()
              const cacheKey = cacheStore.generateKey('get', path, response.config.params)

              // Use the TTL from cacheInfo (5000ms for patterns, 86400000ms for load_new)
              const ttl = cacheInfo.ttl || 86400000 // Default to 24 hours if not specified

              cacheStore.set(
                cacheKey,
                {
                  data: response.data,
                  status: response.status,
                  statusText: response.statusText,
                  headers: response.headers,
                },
                ttl
              )
              cacheLog(
                `Saved to cache (before resolving in-flight) - Path: ${path} | Key: ${cacheKey} | TTL: ${ttl}ms`,
                'save'
              )
              cacheLog(
                `Cache now available for immediate use - Path: ${path} | Key: ${cacheKey}`,
                'save'
              )

              // Mark as already cached to prevent duplicate save in the regular cache section
              response.config._alreadyCached = true
            }
          }
        } catch (cacheErr) {
          cacheLog(
            `Error saving cache before resolving in-flight - Error: ${cacheErr?.message || String(cacheErr)}`,
            'error'
          )
        }

        // Now resolve all waiting requests with this response
        if (resolveInFlight) {
          // Deep copy the response to prevent mutations between requests
          const sharedResponse = {
            ...response,
            data: safeDeepCopy(response.data),
          }

          resolveInFlight(sharedResponse)
          cacheLog(
            `Resolved in-flight request - Path: ${normalizePath(response.config)} | Key: ${inFlightCacheKey} | Status: ${response.status}`,
            'cached'
          )
        }

        // Clean up in-flight tracking
        inFlightRequests.delete(inFlightCacheKey)
      } else {
        // Reject all waiting requests
        if (rejectInFlight) {
          rejectInFlight(response)
          cacheLog(
            `Rejected in-flight request - Path: ${normalizePath(response.config)} | Key: ${inFlightCacheKey} | Status: ${response.status}`,
            'error'
          )
        }

        // Clean up in-flight tracking
        inFlightRequests.delete(inFlightCacheKey)
      }
    }

    //cacheLog(methodString, endpointString, queryParams, response.status, fromCache)
    // Cache population for eligible GET endpoints (2xx only)
    // Skip if response came from cache to avoid re-caching, or if already cached in in-flight handler
    if (!fromCache && !response.config._alreadyCached) {
      try {
        const method = response.config.method ? String(response.config.method).toLowerCase() : 'get'
        if (method === 'get') {
          const path = normalizePath(response.config)
          const cacheInfo = getCacheInfo(path)

          if (cacheInfo.shouldCache && response.status >= 200 && response.status < 300) {
            const cacheStore = useApiCacheStore()
            const cacheKey = cacheStore.generateKey('get', path, response.config.params)

            // Use the TTL from cacheInfo (5000ms for patterns, 86400000ms for load_new)
            const ttl = cacheInfo.ttl || 86400000 // Default to 24 hours if not specified

            cacheStore.set(
              cacheKey,
              {
                data: response.data,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
              },
              ttl
            )
            cacheLog(`Saved to cache - Path: ${path} | Key: ${cacheKey} | TTL: ${ttl}ms`, 'save')
            cacheLog(
              `Cache now available for immediate use - Path: ${path} | Key: ${cacheKey}`,
              'save'
            )
          }
        }
      } catch (cacheErr) {
        cacheLog(
          `Response interceptor cache write error - Error: ${cacheErr?.message || String(cacheErr)}`,
          'error'
        )
      }
    } else {
      // Log when serving from cache (response interceptor path)
      const path = normalizePath(response.config)
      const cacheStore = useApiCacheStore()
      const cacheKey = cacheStore.generateKey('get', path, response.config.params)

      cacheLog(
        `Served from cache (response interceptor) - Path: ${path} | Key: ${cacheKey} | Status: ${response.status}`,
        'cached'
      )
    }

    return response
  },
  async error => {
    rejectReplacedContext(error?.config)

    // Handle in-flight request deduplication on error
    const inFlightCacheKey = error?.config?._inFlightCacheKey
    if (inFlightCacheKey && inFlightRequests.has(inFlightCacheKey)) {
      const rejectInFlight = error?.config?._inFlightReject
      if (rejectInFlight) {
        rejectInFlight(error)
      }

      // Clean up in-flight tracking
      inFlightRequests.delete(inFlightCacheKey)
      cacheLog(
        `In-flight request failed and cleaned up - CacheKey: ${inFlightCacheKey} | Error: ${error?.message || String(error)}`,
        'error'
      )
    }

    if (error?.responseCode === ResponseCode.UNAUTHORIZED && !error?.response)
      return Promise.reject(error)

    const errorResponse = error?.response ?? null

    if (errorResponse) {
      const responseCode = errorResponse.status

      log('w', `[axios] ${responseCode} Error`, errorResponse)

      if (isClientError(responseCode) || isServerError(responseCode)) {
        // `fromResponse` tells an API answer from a transport failure, which is rejected below
        // with a 500 as well.
        const appErr = { ...toAppHttpError(errorResponse), fromResponse: true }

        // Rejected guest service reads recover once; member sessions open sign-in.
        // A plain permission denial remains page-level.
        const sessionEndReason = classifySessionEnd(appErr, error.config)
        const authStore = useAuthStore()
        if (
          sessionEndReason &&
          (await recoverGuestRead(error.config, authStore, sessionEndReason))
        ) {
          return axiosIns.request(error.config)
        }
        if (
          sessionEndReason &&
          error.config?._guestRecoveryAttempted &&
          hasRecoveredGuestRead(error.config, authStore)
        ) {
          authStore.resetGuestAuthForLobby()
        }

        const isGuestRenewal =
          authStore.isGuest &&
          error.config?.url === Endpoint.Auth.refresh.url &&
          error.config?.method === Endpoint.Auth.refresh.method

        if (sessionEndReason && !isGuestRenewal && !error.config?._guestRecoveryAttempted) {
          authStore
            .endSession(sessionEndReason)
            .catch(endError => log('w', '[axios] Session-ended handling failed', endError))
        }
        if (isServerError(responseCode)) {
          log('e', `[axios] Server error (${responseCode}):`, appErr)
        }

        return Promise.reject(appErr)
      }

      // Unexpected path; reject with normalized error
      return Promise.reject({ ...toAppHttpError(errorResponse), fromResponse: true })
    } else if (error?.request) {
      log('w', '[axios] No response received from the server.')
      log('e', '[axios] Error Request:', error.request)

      return Promise.reject({ responseCode: 500, message: 'Network error' })
    } else {
      log('e', 'Error Message:', error?.message)

      return Promise.reject({ responseCode: 500, message: error?.message || 'Request error' })
    }
  }
)

export default axiosIns
